"use client"

import { Head, router } from "@inertiajs/react"
import { MessagePlusCircle, Plus, SearchLg } from "@untitledui/icons"
import { useEffect, useRef, useState } from "react"

import { CollabsTable } from "@/components/admin/collabs/collabs-table"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Input } from "@/components/ui/input"
import { PaginationCardMinimal } from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { adminCollabsPath, newAdminCollabPath } from "@/routes"
import { BriefStatus } from "@/types/contents"
import type { Pagination } from "@/types/contents/pagination"
import type { Brief } from "@/types/serializers"

interface Filters {
  q: string | null
  status: string
}

interface CollabsIndexProps {
  briefs: Brief["brief"][]
  pagination: Pagination
  filters: Filters
}

const STATUS_ITEMS = [
  { id: "all", label: "All" },
  { id: BriefStatus.archived, label: "Archived" },
  { id: BriefStatus.draft, label: "Draft" },
  { id: BriefStatus.published, label: "Published" },
]

/**
 * CollabsIndexProps is an admin page component for showing all collabs.
 * @param briefs - collabs to display
 * @param pagination - current page and total pages
 * @param filters - active search and status filters
 */
export default function CollabsIndex({
  briefs,
  pagination,
  filters,
}: CollabsIndexProps) {
  const isFirstRender = useRef(true)

  const [query, setQuery] = useState(filters.q ?? "")

  const visit = (params: Partial<Filters> & { page?: number }) => {
    router.get(
      adminCollabsPath(),
      {
        q: "q" in params ? (params.q ?? undefined) : (filters.q ?? undefined),
        status: params.status ?? filters.status,
        page: params.page ?? 1,
      },
      { preserveState: true, replace: true },
    )
  }

  // Debounced search input — fires only once typing pauses, and never on mount.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const timeout = setTimeout(() => {
      router.get(
        adminCollabsPath(),
        { q: query || undefined, status: filters.status, page: 1 },
        { preserveState: true, replace: true },
      )
    }, 300)
    return () => clearTimeout(timeout)
  }, [query, filters.status])

  const isFiltered = Boolean(filters.q) || filters.status !== "all"
  return (
    <DashboardLayout>
      <Head title="Collabs" />
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pt-8 pb-12 lg:px-8">
        <header className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
              Collabs
            </h1>
            <p className="text-md text-tertiary">
              Every open call for a community playlist, with its status and
              suggestion count.
            </p>
          </div>
          <Button
            size="sm"
            iconLeading={Plus}
            onClick={() => router.visit(newAdminCollabPath())}
          >
            New collab
          </Button>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            size="sm"
            icon={SearchLg}
            aria-label="Search collabs"
            placeholder="Search by title"
            value={query}
            onChange={setQuery}
            className="sm:max-w-xs"
          />
          <Select
            size="sm"
            aria-label="Filter by status"
            items={STATUS_ITEMS}
            onChange={(key) => key != null && visit({ status: String(key) })}
            className="w-30"
          >
            {(item) => (
              <Select.Item key={item.id} id={item.id}>
                {item.label}
              </Select.Item>
            )}
          </Select>
        </div>

        {briefs.length === 0 ? (
          <EmptyState size="sm" className="py-10">
            <EmptyState.Header pattern="none">
              <EmptyState.FeaturedIcon
                color="gray"
                theme="modern"
                icon={MessagePlusCircle}
              />
            </EmptyState.Header>
            <EmptyState.Content>
              <p className="text-primary text-md text-center font-semibold">
                {isFiltered ? "No collabs match" : "No collabs yet"}
              </p>
              <EmptyState.Description>
                {isFiltered
                  ? "Try a different search or clear the filters."
                  : "Create the first one to open a call for community playlist suggestions."}
              </EmptyState.Description>
            </EmptyState.Content>
            {!isFiltered && (
              <EmptyState.Footer>
                <Button
                  size="sm"
                  iconLeading={Plus}
                  onClick={() => router.visit(newAdminCollabPath())}
                >
                  New collab
                </Button>
              </EmptyState.Footer>
            )}
          </EmptyState>
        ) : (
          <CollabsTable
            rows={briefs}
            aria-label="Collabs"
            footer={
              pagination.total > 1 ? (
                <PaginationCardMinimal
                  page={pagination.page}
                  total={pagination.total}
                  onPageChange={(page) => visit({ page })}
                />
              ) : undefined
            }
          />
        )}
      </main>
    </DashboardLayout>
  )
}
